import type {
  Detencion,
  ControlMedico,
  PertenenciaCustodia,
  OficialSeguridad,
  UnidadPolicial,
  CatalogoInfraccion,
  AudienciaJuzgado,
  CitatorioAudiencia,
  EntregaCitatorio,
  HistoricoJuzgado,
} from '../views/JuezMunicipal/juzgadoTypes';

const API_BASE = `${process.env.REACT_APP_API_URL || 'https://totolac-backend.togui.com.mx'}/api`;

async function req<T = any>(url: string, opts?: RequestInit): Promise<T> {
  const resp = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(opts?.headers ?? {}) },
    ...opts,
  });
  const json = await resp.json();
  if (!json.ok) throw new Error(json.error ?? `HTTP ${resp.status}`);
  return json.data;
}

// ─────────────────────────────────────────────────────────────────────────────
// DETENCIONES
// ─────────────────────────────────────────────────────────────────────────────

export const juzgadoApi = {
  // Detenciones
  async listarDetenciones(params?: { estatus?: string; q?: string; fechaInicio?: string; fechaFin?: string }): Promise<Detencion[]> {
    const qs = new URLSearchParams();
    if (params?.estatus) qs.set('estatus', params.estatus);
    if (params?.q) qs.set('q', params.q);
    if (params?.fechaInicio) qs.set('fechaInicio', params.fechaInicio);
    if (params?.fechaFin) qs.set('fechaFin', params.fechaFin);
    return req(`${API_BASE}/juzgado/detenciones?${qs}`);
  },

  async obtenerDetencion(id: number): Promise<Detencion> {
    return req(`${API_BASE}/juzgado/detenciones/${id}`);
  },

  async crearDetencion(data: Partial<Detencion>): Promise<Detencion> {
    return req(`${API_BASE}/juzgado/detenciones`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async actualizarDetencion(id: number, data: Partial<Detencion>): Promise<Detencion> {
    return req(`${API_BASE}/juzgado/detenciones/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async cambiarEstatusDetencion(id: number, estatus: string, motivo?: string): Promise<Detencion> {
    return req(`${API_BASE}/juzgado/detenciones/${id}/estatus`, {
      method: 'PATCH',
      body: JSON.stringify({ estatus, motivo }),
    });
  },

  async historicoDetencion(id: number): Promise<HistoricoJuzgado[]> {
    return req(`${API_BASE}/juzgado/detenciones/${id}/historico`);
  },

  async subirArchivosDetencion(id: number, files: File[], tipo: string): Promise<any[]> {
    const fd = new FormData();
    files.forEach(f => fd.append('archivos', f));
    fd.append('tipo', tipo);
    const resp = await fetch(`${API_BASE}/juzgado/detenciones/${id}/archivos`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    const json = await resp.json();
    if (!json.ok) throw new Error(json.error);
    return json.data;
  },

  async listarArchivosDetencion(id: number): Promise<any[]> {
    return req(`${API_BASE}/juzgado/detenciones/${id}/archivos`);
  },

  // Control Médico
  async crearControlMedico(detencionId: number, data: Partial<ControlMedico>): Promise<ControlMedico> {
    return req(`${API_BASE}/juzgado/detenciones/${detencionId}/control-medico`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async obtenerControlMedico(detencionId: number): Promise<ControlMedico> {
    return req(`${API_BASE}/juzgado/detenciones/${detencionId}/control-medico`);
  },

  async actualizarControlMedico(id: number, data: Partial<ControlMedico>): Promise<ControlMedico> {
    return req(`${API_BASE}/juzgado/control-medico/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Pertenencias
  async crearPertenencia(detencionId: number, data: Partial<PertenenciaCustodia>): Promise<PertenenciaCustodia> {
    return req(`${API_BASE}/juzgado/detenciones/${detencionId}/pertenencias`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async listarPertenencias(detencionId: number): Promise<PertenenciaCustodia[]> {
    return req(`${API_BASE}/juzgado/detenciones/${detencionId}/pertenencias`);
  },

  async registrarSalidaPertenencia(id: number, data: Partial<PertenenciaCustodia>): Promise<PertenenciaCustodia> {
    return req(`${API_BASE}/juzgado/pertenencias/${id}/salida`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  // ─────────────────────────────────────────────────────────────────────────
  // CATÁLOGOS
  // ─────────────────────────────────────────────────────────────────────────
  async listarCatalogosInfraccion(): Promise<CatalogoInfraccion[]> {
    return req(`${API_BASE}/juzgado/catalogos/infracciones-transito`);
  },

  async crearCatalogoInfraccion(data: Partial<CatalogoInfraccion>): Promise<CatalogoInfraccion> {
    return req(`${API_BASE}/juzgado/catalogos/infracciones-transito`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async listarOficiales(): Promise<OficialSeguridad[]> {
    return req(`${API_BASE}/juzgado/catalogos/oficiales`);
  },

  async crearOficial(data: Partial<OficialSeguridad>): Promise<OficialSeguridad> {
    return req(`${API_BASE}/juzgado/catalogos/oficiales`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async listarUnidades(): Promise<UnidadPolicial[]> {
    return req(`${API_BASE}/juzgado/catalogos/unidades`);
  },

  async crearUnidad(data: Partial<UnidadPolicial>): Promise<UnidadPolicial> {
    return req(`${API_BASE}/juzgado/catalogos/unidades`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // ─────────────────────────────────────────────────────────────────────────
  // INFRACCIONES
  // ─────────────────────────────────────────────────────────────────────────
  async listarInfracciones(params?: { q?: string; estatus?: string; fechaInicio?: string; fechaFin?: string }): Promise<any[]> {
    const qs = new URLSearchParams();
    if (params?.q) qs.set('q', params.q);
    if (params?.estatus) qs.set('estatus', params.estatus);
    if (params?.fechaInicio) qs.set('fechaInicio', params.fechaInicio);
    if (params?.fechaFin) qs.set('fechaFin', params.fechaFin);
    return req(`${API_BASE}/juzgado/infracciones?${qs}`);
  },

  async listarPendientesCalificar(): Promise<any[]> {
    return req(`${API_BASE}/juzgado/infracciones/pendientes-calificar`);
  },

  async obtenerInfraccion(id: number): Promise<any> {
    return req(`${API_BASE}/juzgado/infracciones/${id}`);
  },

  async sugerirClasificacionIA(id: number): Promise<any> {
    return req(`${API_BASE}/juzgado/infracciones/${id}/sugerir-clasificacion-ia`, { method: 'POST' });
  },

  async clasificarInfraccion(id: number, data: any): Promise<any> {
    return req(`${API_BASE}/juzgado/infracciones/${id}/clasificar`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async calificarSancion(id: number, data: any): Promise<any> {
    return req(`${API_BASE}/juzgado/infracciones/${id}/calificar-sancion`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async generarResolucion(id: number): Promise<any> {
    return req(`${API_BASE}/juzgado/infracciones/${id}/generar-resolucion`, { method: 'POST' });
  },

  async aplicarDescuento(sancionId: number, data: any): Promise<any> {
    return req(`${API_BASE}/juzgado/infracciones/sancion/${sancionId}/descuento`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // ─────────────────────────────────────────────────────────────────────────
  // AUDIENCIAS
  // ─────────────────────────────────────────────────────────────────────────
  async listarAudiencias(params?: { estatus?: string; q?: string; fechaInicio?: string; fechaFin?: string }): Promise<AudienciaJuzgado[]> {
    const qs = new URLSearchParams();
    if (params?.estatus) qs.set('estatus', params.estatus);
    if (params?.q) qs.set('q', params.q);
    if (params?.fechaInicio) qs.set('fechaInicio', params.fechaInicio);
    if (params?.fechaFin) qs.set('fechaFin', params.fechaFin);
    return req(`${API_BASE}/juzgado/audiencias?${qs}`);
  },

  async obtenerAudiencia(id: number): Promise<AudienciaJuzgado> {
    return req(`${API_BASE}/juzgado/audiencias/${id}`);
  },

  async crearAudiencia(data: Partial<AudienciaJuzgado>): Promise<AudienciaJuzgado> {
    return req(`${API_BASE}/juzgado/audiencias`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async actualizarAudiencia(id: number, data: Partial<AudienciaJuzgado>): Promise<AudienciaJuzgado> {
    return req(`${API_BASE}/juzgado/audiencias/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async cambiarEstatusAudiencia(id: number, estatus: string, motivo?: string): Promise<AudienciaJuzgado> {
    return req(`${API_BASE}/juzgado/audiencias/${id}/estatus`, {
      method: 'PATCH',
      body: JSON.stringify({ estatus, motivo }),
    });
  },

  async historicoAudiencia(id: number): Promise<HistoricoJuzgado[]> {
    return req(`${API_BASE}/juzgado/audiencias/${id}/historico`);
  },

  // Citatorios
  async crearCitatorio(audienciaId: number, data: Partial<CitatorioAudiencia>): Promise<CitatorioAudiencia> {
    return req(`${API_BASE}/juzgado/audiencias/${audienciaId}/citatorios`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async listarCitatorios(audienciaId: number): Promise<CitatorioAudiencia[]> {
    return req(`${API_BASE}/juzgado/audiencias/${audienciaId}/citatorios`);
  },

  async registrarEntregaCitatorio(citatorioId: number, data: Partial<EntregaCitatorio>): Promise<EntregaCitatorio> {
    return req(`${API_BASE}/juzgado/citatorios/${citatorioId}/entregas`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async listarEntregasCitatorio(citatorioId: number): Promise<EntregaCitatorio[]> {
    return req(`${API_BASE}/juzgado/citatorios/${citatorioId}/entregas`);
  },

  // ─────────────────────────────────────────────────────────────────────────
  // PDFs
  // ─────────────────────────────────────────────────────────────────────────
  pdfActaDetencion(id: number): string {
    return `${API_BASE}/juzgado/detenciones/${id}/pdf/acta`;
  },

  pdfCertificadoMedico(id: number): string {
    return `${API_BASE}/juzgado/detenciones/${id}/pdf/certificado-medico`;
  },

  pdfCitatorio(id: number): string {
    return `${API_BASE}/juzgado/citatorios/${id}/pdf`;
  },

  pdfInformeSancion(multaId: number): string {
    return `${API_BASE}/juzgado/infracciones/${multaId}/informe-sancion`;
  },

  pdfBoletaInfraccion(multaId: number): string {
    return `${API_BASE}/juzgado/infracciones/${multaId}/boleta`;
  },

  pdfInventarioPertenencias(detencionId: number): string {
    return `${API_BASE}/juzgado/detenciones/${detencionId}/pdf/inventario`;
  },

  pdfReporteDetenciones(params?: { fechaInicio?: string; fechaFin?: string; estatus?: string; oficialId?: number }): string {
    const qs = new URLSearchParams();
    if (params?.fechaInicio) qs.set('fechaInicio', params.fechaInicio);
    if (params?.fechaFin) qs.set('fechaFin', params.fechaFin);
    if (params?.estatus) qs.set('estatus', params.estatus);
    if (params?.oficialId) qs.set('oficialId', String(params.oficialId));
    return `${API_BASE}/juzgado/detenciones/reporte?${qs}`;
  },

  // ─────────────────────────────────────────────────────────────────────────
  // DESARROLLO DE AUDIENCIA EN TIEMPO REAL
  // ─────────────────────────────────────────────────────────────────────────
  async obtenerActa(audienciaId: number): Promise<any> {
    return req(`${API_BASE}/juzgado/audiencias/${audienciaId}/acta`);
  },

  async guardarActa(audienciaId: number, data: any): Promise<any> {
    return req(`${API_BASE}/juzgado/audiencias/${audienciaId}/acta`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async listarCheckIns(audienciaId: number): Promise<any[]> {
    return req(`${API_BASE}/juzgado/audiencias/${audienciaId}/checkins`);
  },

  async crearCheckIn(audienciaId: number, data: any): Promise<any> {
    return req(`${API_BASE}/juzgado/audiencias/${audienciaId}/checkins`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async subirIdentificacionCheckIn(checkinId: number, archivo: File): Promise<any> {
    const fd = new FormData();
    fd.append('archivo', archivo);
    const resp = await fetch(`${API_BASE}/juzgado/checkins/${checkinId}/identificacion`, {
      method: 'POST', credentials: 'include', body: fd,
    });
    const json = await resp.json();
    if (!json.ok) throw new Error(json.error);
    return json.data;
  },

  async listarDocsPresentados(audienciaId: number): Promise<any[]> {
    return req(`${API_BASE}/juzgado/audiencias/${audienciaId}/documentos-presentados`);
  },

  async crearDocPresentado(audienciaId: number, data: { tipo?: string; nombre: string; descripcion?: string; contenidoTexto?: string; presentadoPor?: string }, archivo?: File): Promise<any> {
    if (archivo) {
      const fd = new FormData();
      fd.append('archivo', archivo);
      Object.entries(data).forEach(([k, v]) => v != null && fd.append(k, String(v)));
      const resp = await fetch(`${API_BASE}/juzgado/audiencias/${audienciaId}/documentos-presentados`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const json = await resp.json();
      if (!json.ok) throw new Error(json.error);
      return json.data;
    }
    return req(`${API_BASE}/juzgado/audiencias/${audienciaId}/documentos-presentados`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Aplicar descuento manual a una sanción
  async aplicarDescuentoSancion(sancionId: number, data: { tipo?: string; porcentaje?: number; monto?: number; motivo: string; motivoDetalle?: string }): Promise<any> {
    return req(`${API_BASE}/juzgado/infracciones/sancion/${sancionId}/descuento`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Subir archivos a una detención (fotos, identificación)
  async subirArchivosDetencionTipo(detencionId: number, files: File[], tipo: string): Promise<any[]> {
    const fd = new FormData();
    files.forEach(f => fd.append('archivos', f));
    fd.append('tipo', tipo);
    const resp = await fetch(`${API_BASE}/juzgado/detenciones/${detencionId}/archivos`, {
      method: 'POST', credentials: 'include', body: fd,
    });
    const json = await resp.json();
    if (!json.ok) throw new Error(json.error);
    return json.data;
  },

  // ─────────────────────────────────────────────────────────────────────────
  // TESORERÍA / CAJA
  // ─────────────────────────────────────────────────────────────────────────
  async listarNotasCobro(params?: { q?: string; folio?: string }): Promise<any[]> {
    const qs = new URLSearchParams();
    if (params?.q) qs.set('q', params.q);
    if (params?.folio) qs.set('folio', params.folio);
    return req(`${API_BASE}/tesoreria/juzgado/notas-cobro?${qs}`);
  },

  async obtenerNotaCobro(folio: string): Promise<any> {
    return req(`${API_BASE}/tesoreria/juzgado/notas-cobro/${folio}`);
  },

  async registrarPago(sancionId: number, data: {
    monto: number;
    metodoPago: string;
    cajero?: string;
    folioRecibo?: string;
    uuidCfdi?: string;
    facturar?: boolean;
    facturaData?: { rfc: string; nombre?: string; codigoPostal?: string; regimenFiscal?: string; usoCfdi?: string; email?: string };
  }): Promise<any> {
    return req(`${API_BASE}/tesoreria/juzgado/notas-cobro/${sancionId}/pagar`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Portal: validación pública
  async validarCitatorioPortal(folio: string): Promise<any> {
    return req(`${API_BASE}/portal/juzgado/citatorios/${folio}/validar`);
  },

  async validarInfraccionPortal(folio: string): Promise<any> {
    return req(`${API_BASE}/portal/juzgado/infracciones/${folio}/validar`);
  },
};

export default juzgadoApi;
